import {
  onScopeDispose,
  readonly,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue'

/** The typed text changes at most this often, so a long page never costs a render a frame. */
const UPDATE_INTERVAL_MS = 40
/** The gap assumed before two drafts have arrived (the backend sends about one a second). */
const DEFAULT_DRAFT_GAP_MS = 1000
/** Typing finishes a little before the next draft is due, so it never falls behind. */
const DRAFT_GAP_SHARE = 0.9

const BODY_START = /<body[\s>]/i

/** A draft's head (everything before `<body`) and body. */
const splitAtBody = (html: string): [string, string] => {
  const index = html.search(BODY_START)
  return index < 0 ? ['', html] : [html.slice(0, index), html.slice(index)]
}

/**
 * A live draft, typed out. Drafts of a page being built arrive whole, about once a second;
 * shown as they come, the page jumps a chunk at a time. This plays the new part of each draft
 * out over the gap until the next one, so it reads like the page is being written.
 *
 * Only the body is typed: the head holds the page's compiled CSS, which grows with every new
 * class, and changes at once. A draft that is not the last one plus more (a rewrite, an edit)
 * shows at once too. While `isActive` is false the source passes straight through.
 */
export function useDraftTypewriter(
  source: MaybeRefOrGetter<string>,
  isActive: MaybeRefOrGetter<boolean>,
) {
  const text = ref(toValue(source))

  let head = ''
  let body = ''
  /** How far typing has got into `body`; fractional, so a slow rate still keeps time. */
  let typedPosition = 0
  /** How much of `body` is shown. */
  let typedLength = 0
  let charsPerMs = 0
  let lastDraftAt = 0
  let draftGapMs = DEFAULT_DRAFT_GAP_MS
  let lastUpdateAt = 0
  let frameId: number | null = null

  const show = () => {
    text.value = head + body.slice(0, typedLength)
  }

  const stop = () => {
    if (frameId !== null) cancelAnimationFrame(frameId)
    frameId = null
  }

  const tick = (now: number) => {
    const elapsed = now - lastUpdateAt
    if (elapsed >= UPDATE_INTERVAL_MS) {
      lastUpdateAt = now
      typedPosition = Math.min(
        body.length,
        typedPosition + elapsed * charsPerMs,
      )
      const nextLength = Math.floor(typedPosition)
      if (nextLength !== typedLength) {
        typedLength = nextLength
        show()
      }
    }
    frameId = typedLength < body.length ? requestAnimationFrame(tick) : null
  }

  const start = () => {
    if (frameId !== null) return
    lastUpdateAt = performance.now()
    frameId = requestAnimationFrame(tick)
  }

  const reset = () => {
    stop()
    head = ''
    body = ''
    typedPosition = 0
    typedLength = 0
    lastDraftAt = 0
    draftGapMs = DEFAULT_DRAFT_GAP_MS
  }

  watch(
    [() => toValue(source), () => toValue(isActive)],
    ([draft, active]) => {
      if (!active) {
        reset()
        text.value = draft
        return
      }
      const now = performance.now()
      // The first draft shows at once: it is what opened the preview.
      const isFirstDraft = !lastDraftAt
      if (!isFirstDraft) {
        draftGapMs = Math.min(3000, Math.max(300, now - lastDraftAt))
      }
      lastDraftAt = now

      const [nextHead, nextBody] = splitAtBody(draft)
      const isContinuation = nextBody.startsWith(body.slice(0, typedLength))
      head = nextHead
      body = nextBody
      if (isFirstDraft || !isContinuation) typedLength = body.length
      typedPosition = typedLength
      charsPerMs = (body.length - typedLength) / (draftGapMs * DRAFT_GAP_SHARE)
      show()
      if (typedLength < body.length) start()
      else stop()
    },
    { immediate: true },
  )

  onScopeDispose(stop)

  return { text: readonly(text) }
}
